import { AreaLoader, DropList, DropListItem, DropListItemCheckbox, Highlighted, setAnnouncement } from '@starrez/starryui';
import * as React from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { IAvailableFilter, IData } from '..';
import { strings } from '../../../../.localisation';
import { getResultCountMessage } from '../../../utils/stringUtil';
import { WrapperDiv } from '../FilterBar';
import { useFilterBarContext } from '../FilterBar.context';
import { NoneFound } from './AddFilterButton';
import { DisplayFilterButton } from './DisplayFilterButton';

export interface ISelectFilterDropdownProps {
    filter: IAvailableFilter;
    onSelected: (filter: IAvailableFilter, value: IData[]) => void;
    onRemove: (filter: IAvailableFilter) => void;
}

export const SelectFilterDropdown = ({ onSelected, onRemove, filter }: ISelectFilterDropdownProps) => {
    const [dropdownData, setDropdownData] = React.useState<IData[]>();
    const [visible, setVisible] = React.useState(filter.showOptions);
    const [isLoading, setIsLoading] = React.useState(false);

    const buttonRef = React.createRef<HTMLButtonElement>();

    const { onFilterTypeAdd } = useFilterBarContext();

    const handleItemRemove = () => {
        onRemove(filter);
    };

    const handlePickFilter = () => {
        setVisible(true);
        setIsLoading(true);
        onFilterTypeAdd(filter).then((result) => {
            setDropdownData(result);
            setIsLoading(false);
        });
    };

    const handleCompleted = (items: IData[]) => {
        setVisible(false);
        onSelected(filter, items);

        if (buttonRef.current) {
            buttonRef.current.focus();
        }
    };

    React.useEffect(() => {
        if (onFilterTypeAdd) {
            setIsLoading(true);
            onFilterTypeAdd(filter).then((result) => {
                setDropdownData(result);
                setIsLoading(false);
            });
        }
    }, []);

    return (
        <Manager>
            <Reference>
                {({ ref }) => (
                    <div ref={ref}>
                        <DisplayFilterButton
                            ref={buttonRef}
                            dropdownVisible={Boolean(visible)}
                            onRemove={handleItemRemove}
                            onPickFilter={handlePickFilter}
                            active={Boolean(visible)}
                            caption={filter.name}
                            value={filter.value ? filter.value : []}
                        />
                    </div>
                )}
            </Reference>
            <Popper placement="bottom-start">
                {({ ref, style }) => (
                    <div ref={ref} style={{ ...style, zIndex: 2 }}>
                        {visible &&
                            (isLoading ? (
                                <DropList>
                                    <DropListItem
                                        OnClick={() => {
                                            /*Empty*/
                                        }}
                                        Title={strings.Label_Loading}
                                    >
                                        <AreaLoader />
                                    </DropListItem>
                                </DropList>
                            ) : (
                                dropdownData && <ListPicker Filter={filter} Data={dropdownData} OnCompleted={handleCompleted} />
                            ))}
                    </div>
                )}
            </Popper>
        </Manager>
    );
};

function useOutsideAlerter(ref: React.RefObject<HTMLElement>, onClick: () => void) {
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                onClick();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref]);
}

const ListPicker = ({ Filter, Data, OnCompleted }: { Filter: IAvailableFilter; Data: IData[]; OnCompleted: (items: IData[]) => void }) => {
    const [selectedItems, setSelectedItems] = React.useState<IData[]>(Filter.value ? [...Filter.value] : []);
    const [searchString, setSearchString] = React.useState('');
    const containerRef = React.createRef<HTMLDivElement>();
    const { sayPolitely } = setAnnouncement();

    const orderByCaption = (dataSource: IData[]): IData[] => {
        const hasEmptyCaption = dataSource.find((i: IData) => !i.caption || !i.caption?.length);

        if (hasEmptyCaption) {
            const dataWithEmptyCaption: IData[] = [];

            const filteredData = dataSource.filter((i) => {
                if (!i.caption || !i.caption?.length) {
                    dataWithEmptyCaption.push({
                        ...i,
                        caption: strings.FilterBar_Label_NotProvided,
                    });

                    return false;
                }

                return true;
            });

            return filteredData.concat(dataWithEmptyCaption);
        }

        return dataSource;
    };

    const [data, setData] = React.useState<IData[]>(orderByCaption(Data));

    useOutsideAlerter(containerRef, () => OnCompleted(selectedItems));

    const handleOnCloseMenu = () => {
        if (Filter.value) {
            OnCompleted(Filter.value);
        }
    };

    const handleSelectSingleItem = (item: IData) => {
        OnCompleted([item]);
    };

    const handleMultipleClick = (checked: boolean, item: IData) => {
        if (checked) {
            selectedItems.push(item);
            setSelectedItems([...selectedItems]);
        } else {
            const index = selectedItems.findIndex((e) => e.value === item.value);
            selectedItems.splice(index, 1);
            setSelectedItems([...selectedItems]);
        }
    };

    const isItemSelected = (item: IData) => {
        const isSelected = selectedItems.find((e) => e.value === item.value) !== undefined;
        return isSelected;
    };

    const handleOnConfirmClick = () => {
        OnCompleted(selectedItems);
    };

    const handleSearched = (search: string) => {
        const regex = RegExp(search, 'ig');
        const newData = orderByCaption(Data)
            .filter((e) => e.caption)
            .filter((e) => e.caption.match(regex));

        setSearchString(search);
        setData(newData);

        sayPolitely(getResultCountMessage(newData.length) as string);

        return Promise.resolve();
    };

    const handleSearchCancelled = () => {
        setSearchString('');
        setData(Data);
        return Promise.resolve();
    };

    return (
        <WrapperDiv ref={containerRef}>
            <DropList
                MaxHeight={400}
                ShowConfirm={Filter.multiple}
                ConfirmCaption={strings.Action_Close}
                ShowSearch={Data.length > 8}
                OnSearch={handleSearched}
                OnSearchCancel={handleSearchCancelled}
                OnConfirmClick={handleOnConfirmClick}
                OnCloseMenu={handleOnCloseMenu}
            >
                {data.length > 0 ? (
                    data.map((item) =>
                        !Filter.multiple ? (
                            <DropListItem key={item.value} OnClick={() => handleSelectSingleItem(item)} Selected={isItemSelected(item)}>
                                <Highlighted Text={item.caption ? item.caption : ''} Highlight={searchString} />
                            </DropListItem>
                        ) : (
                            <DropListItemCheckbox
                                key={item.value}
                                Value={isItemSelected(item)}
                                OnClick={(checked) => handleMultipleClick(checked, item)}
                            >
                                <Highlighted Text={item.caption ? item.caption : ''} Highlight={searchString} />
                            </DropListItemCheckbox>
                        )
                    )
                ) : (
                    <NoneFound />
                )}
            </DropList>
        </WrapperDiv>
    );
};
